import { useNavigate } from "react-router-dom";
import {
  useProjectsList,
  useDashboardStats,
  useUpcomingTasks,
} from "../../../lib/react-query/projects";
import { useUserNetworkCount } from "../../../lib/react-query/partners.ts";
import { useProfileCompletion, useProfileDetails } from "../../../lib/react-query/profile";
import StatsCards from "../../../components/Dashboard/StatsCards";

import QuickActions from "../../../components/Dashboard/QuickActions";
import OnboardingChecklist from "../../../components/Dashboard/OnboardingChecklist";
import ActiveProjects from "../../../components/Dashboard/ActiveProjects";

import UpcomingDeadlines from "../../../components/Dashboard/UpcomingDeadlines";
import RecentActivity from "../../../components/Dashboard/RecentActivity";
import { useIsClient, useCurrentProject } from "../../../hooks/useClientRole";
import { Button } from "../../../components/shared/Button";
import { Card } from "../../../components/shared/Card";
import { EmptyState } from "../../../components/shared/EmptyState";
import { ProjectsIcon, CalendarIcon, PersonIcon } from "../../../assets/icons";
import { PROJECT_GRADIENTS } from "../../../constants/gradients";
import { toUsDate } from "../../../utils/utils";

const PROJECT_STATUS_DISPLAY = {
  ACTIVE: "Active",
  PENDING: "Pending",
  DONE: "Completed",
};

const getProjectInitials = (title) => {
  if (!title) return "P";
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "P";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { currentProject, isLoading } = useCurrentProject();
  const { data: upcomingData, isLoading: isLoadingUpcoming } = useUpcomingTasks(30, 10, true);
  const { data: profileData } = useProfileDetails(true);

  const upcomingTasks = upcomingData?.tasks || [];
  const firstName = profileData?.first_name || profileData?.name?.split(" ")[0] || "";
  const completion = currentProject?.completion_percentage ?? 0;
  const dueLabel = toUsDate(currentProject?.end_date);
  const ownerName = currentProject?.owner?.name;
  const ownerImage = currentProject?.owner?.profile_image_url;
  const projectInitials = getProjectInitials(currentProject?.title);
  const statusKey = currentProject?.status;
  const statusLabel = PROJECT_STATUS_DISPLAY[statusKey] || statusKey || "—";
  const projectGradient =
    PROJECT_GRADIENTS[currentProject?.header_gradient] || "from-primary to-secondary-dark";
  const statusDotColor =
    statusKey === "ACTIVE"
      ? "bg-success"
      : statusKey === "PENDING"
        ? "bg-warning/100"
        : statusKey === "DONE"
          ? "bg-secondary"
          : "bg-mediumgrey";

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 pb-16 lg:p-8 lg:pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-surface-hover rounded-lg w-64" />
            <div className="h-48 bg-surface-hover rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 pb-16 lg:p-8 lg:pb-20">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary">
            Welcome back{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="text-mediumgrey mt-1">
            Here&apos;s your project status at a glance.
          </p>
        </div>
        {currentProject ? (
          <Card variant="elevated" radius="lg" className="overflow-hidden">
            <div className={`relative overflow-hidden bg-gradient-to-r ${projectGradient} px-6 py-5 md:px-8`}>
              <div className="relative z-10 flex items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-white/25">
                    <ProjectsIcon width={22} height={22} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/75">
                      Current project
                    </p>
                    <p className="mt-1 truncate text-sm font-medium text-white/95">
                      {statusLabel}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white shadow-sm">
                  Client
                </span>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-8">
                <div className="flex min-w-0 flex-1 items-center gap-5">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/5 text-primary ring-1 ring-primary/10">
                    <span className="text-xl font-semibold tracking-wide">{projectInitials}</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-2xl font-bold text-primary">
                      {currentProject.title}
                    </h2>
                    <p className="mt-1 line-clamp-2 text-sm text-mediumgrey">
                      {currentProject.description ||
                        "Project details will appear here once the team shares them."}
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => navigate(`/projects/${currentProject.id}/tasks`)}
                  variant="filled"
                  color="secondary"
                  size="lg"
                  className="w-full shrink-0 px-8 shadow-[0_8px_18px_rgba(56,198,255,0.16)] lg:w-auto"
                >
                  View Project Progress
                </Button>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-y-5 border-t border-surface-hover pt-5 lg:grid-cols-4 lg:divide-x lg:divide-surface-hover">
                <div className="lg:pr-5">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-mediumgrey">Status</p>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${statusDotColor}`} aria-hidden />
                    <span className="text-base font-semibold text-darkgrey">{statusLabel}</span>
                  </div>
                </div>

                <div className="lg:px-5">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-mediumgrey">Progress</p>
                  <div className="flex items-center gap-3">
                    <span className="text-base font-semibold tabular-nums text-darkgrey">{completion}%</span>
                    <div className="h-1.5 max-w-[110px] flex-1 overflow-hidden rounded-full bg-surface-hover">
                      <div className="h-1.5 rounded-full bg-primary transition-all duration-500" style={{ width: `${completion}%` }} />
                    </div>
                  </div>
                </div>

                <div className="lg:px-5">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-mediumgrey">Due date</p>
                  <div className="flex items-center gap-2 text-base font-semibold text-darkgrey">
                    <CalendarIcon width={17} height={17} className="text-primary" />
                    <span className="tabular-nums">{dueLabel || "Not set"}</span>
                  </div>
                </div>

                <div className="lg:pl-5">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-mediumgrey">Project manager</p>
                  <div className="flex items-center gap-2 text-base font-semibold text-darkgrey">
                    {ownerImage ? (
                      <img src={ownerImage} alt="" className="h-5 w-5 rounded-full object-cover" />
                    ) : (
                      <PersonIcon width={17} height={17} className="text-primary" />
                    )}
                    <span className="truncate">{ownerName || "Unassigned"}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <EmptyState
            icon={ProjectsIcon}
            title="No active project yet"
            description="Your account is limited to invited projects. You'll see your project here once a manager assigns or activates one."
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <RecentActivity />
          </div>
          <div>
            <UpcomingDeadlines tasks={upcomingTasks} isLoading={isLoadingUpcoming} />
          </div>
        </div>
      </div>
    </div>
  );
};

const BusinessDashboard = () => {
  const { data: projectsData, isLoading: isLoadingProjects } = useProjectsList(
    { ordering: "-updated_at", page_size: 100 },
    true
  );

  const { data: upcomingData, isLoading: isLoadingUpcoming } = useUpcomingTasks(30, 10, true);
  const { data: dashboardStats, isLoading: isLoadingStats } = useDashboardStats(true);
  const { data: networkData, isLoading: isLoadingNetwork } = useUserNetworkCount(true);

  const { data: profileData } = useProfileCompletion(true);
  const { data: userDetails } = useProfileDetails(true);

  const profileCompletion = profileData?.percentage || 0;
  const projects = projectsData?.items || [];
  const stats = dashboardStats || {};
  const networkCount = networkData?.count || 0;
  const firstName = userDetails?.first_name || userDetails?.name?.split(" ")[0] || "";
  const upcomingTasks = upcomingData?.tasks || [];

  const isLoading = isLoadingProjects || isLoadingStats || isLoadingNetwork;

  if (isLoading) {
    return (
      <div className="min-h-screen p-6 pb-16 lg:p-8 lg:pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-surface-hover rounded-lg w-64" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-surface-hover rounded-2xl" />
              ))}
            </div>
            <div className="h-12 bg-surface-hover rounded-xl" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 h-48 bg-surface-hover rounded-2xl" />
              <div className="h-48 bg-surface-hover rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 pb-16 lg:p-8 lg:pb-20">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary">Home</h1>
          <p className="text-mediumgrey mt-1">
            Welcome back{firstName ? `, ${firstName}` : ""}. Here&apos;s what&apos;s happening with your business today.
          </p>
        </div>

        <section className="mb-8">
          <StatsCards
            activeProjects={stats.projects?.active || 0}
            totalProjects={stats.projects?.total || 0}
            pendingTasks={stats.tasks?.pending || 0}
            completedTasks={stats.tasks?.done || 0}
            network={networkCount}
            pendingConnections={stats.pending_connections || 0}
            clients={stats.clients?.total || 0}
            files={stats.files?.total || 0}
          />
        </section>

        <section className="mb-8">
          <QuickActions />
        </section>

        {profileCompletion <= 50 && (
          <section className="mb-8">
            <OnboardingChecklist profileCompletion={profileCompletion} />
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <ActiveProjects
              projects={projects}
              isLoading={isLoadingProjects}
            />
            <RecentActivity />
            <div className="h-6" />
          </div>

          <div className="space-y-6">
            <UpcomingDeadlines
              tasks={upcomingTasks}
              isLoading={isLoadingUpcoming}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const NewDashboard = () => {
  const { isClient: isClientModeActive } = useIsClient();

  if (isClientModeActive) {
    return <ClientDashboard />;
  }

  return <BusinessDashboard />;
};

export default NewDashboard;
